import type { UserRuleScope } from '../../../types/cognitiveProfile'

export interface UserRuleCandidate {
  text: string
  suggestedScope: UserRuleScope
  trigger: string
}

/**
 * Every pattern anchors to string start (optionally after "please") so we
 * only catch IMPERATIVE phrasing. "I always X" / "The team never Y" are not
 * matches — they are self-describing, not user rules.
 */
const IMPERATIVE_PHRASES: Array<{ pattern: RegExp; trigger: string }> = [
  { pattern: /^(?:please\s+)?from now on[,:]?\s+(.+)$/i, trigger: 'from now on' },
  { pattern: /^(?:please\s+)?always\s+(.+)$/i, trigger: 'always' },
  { pattern: /^(?:please\s+)?never\s+(.+)$/i, trigger: 'never' },
  { pattern: /^(?:please\s+)?remember(?:[:,]|\s+that|\s+this[:,]?)?\s+(.+)$/i, trigger: 'remember' },
  { pattern: /^(?:please\s+)?every time (?:you|line)\s+(.+)$/i, trigger: 'every time' },
  { pattern: /^(?:please\s+)?make sure (?:to|you)\s+(.+)$/i, trigger: 'make sure' },
  { pattern: /^(?:please\s+)?don['’]?t (?:ever\s+)?(.+)$/i, trigger: 'don\'t' },
]

// Stems (no trailing \b) so "generate", "plans", "editing", etc. all match.
const EDIT_HINTS = /\b(edit|revis|refin|rewrit|patch|modif|tweak|updat)/i
const CREATE_HINTS = /\b(generat|build|creat|plan|draft|scaffold|outlin)/i

const cleanupSentence = (raw: string): string => {
  const stripped = raw
    .split(/[.!?\n]/)[0]
    .trim()
    .replace(/^["']|["']$/g, '')
    .replace(/\s+/g, ' ')
  return stripped.length > 400 ? `${stripped.slice(0, 397)}…` : stripped
}

const inferScope = (context: string): UserRuleScope => {
  const lower = context.toLowerCase()
  const edit = EDIT_HINTS.test(lower)
  const create = CREATE_HINTS.test(lower)
  if (edit && !create) return 'EDIT'
  if (create && !edit) return 'CREATE'
  return 'ALL'
}

/**
 * Scan a single user chat turn for an intent to persist a rule
 * ("always X", "from now on Y", "remember Z"). Returns the extracted
 * rule text + suggested scope, or null if no imperative phrasing found.
 *
 * Returns AT MOST one candidate per turn — surfacing multiple chips
 * for one message overwhelms the composer.
 */
export const detectRuleCandidate = (text: string): UserRuleCandidate | null => {
  const trimmed = text?.trim() ?? ''
  if (trimmed.length < 6 || trimmed.length > 2_000) return null
  for (const { pattern, trigger } of IMPERATIVE_PHRASES) {
    const match = pattern.exec(trimmed)
    if (!match) continue
    const body = cleanupSentence(match[1] ?? '')
    if (body.length < 4) continue
    const ruleText = trigger === 'never' || trigger === 'don\'t'
      ? `${trigger === 'never' ? 'Never' : 'Do not'} ${body}`
      : trigger === 'always'
        ? `Always ${body}`
        : body.charAt(0).toUpperCase() + body.slice(1)
    return {
      text: ruleText,
      suggestedScope: inferScope(trimmed),
      trigger,
    }
  }
  return null
}
